import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Computer as ComputerIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import { userProductHardwareService } from "../../services/userProductHardwareService";
import AdminInfoDisplay from "./AdminInfoDisplay";

import { useNavigate } from "react-router-dom";

const HardwareSubmissionManagement = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    loadSubmissions();
  }, [page, rowsPerPage, search, statusFilter]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search,
        status: statusFilter,
      };

      const response =
        await userProductHardwareService.getAllHardwareSubmissions(params);
      setSubmissions(response.submissions || []);
      setTotalCount(response.pagination?.total || 0);
      setError(null);
    } catch (err) {
      console.error("Error loading hardware submissions:", err);
      setError("Failed to load hardware submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setReviewDialogOpen(true);
  };

  const handleConfirmSubmission = async (approve) => {
    if (!selectedSubmission) return;

    try {
      setProcessing(true);
      await userProductHardwareService.confirmHardwareId(
        selectedSubmission.id,
        approve,
      );

      setSuccess(
        approve
          ? "Hardware ID confirmed successfully!"
          : "Hardware ID rejected. User can resubmit.",
      );
      setReviewDialogOpen(false);
      setSelectedSubmission(null);

      // Reload submissions
      await loadSubmissions();
    } catch (err) {
      console.error("Error confirming hardware ID:", err);
      setError(
        err.response?.data?.message ||
          "Failed to process hardware ID submission",
      );
    } finally {
      setProcessing(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification("Hardware ID copied successfully!");
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      showNotification("Failed to copy to clipboard", "error");
    }
  };

  const getStatusChip = (submission) => {
    if (submission.is_confirmed && submission.is_locked) {
      return (
        <Chip
          size="small"
          label="Confirmed"
          color="success"
          icon={<CheckIcon />}
        />
      );
    }

    if (submission.is_locked) {
      return <Chip size="small" label="Locked" color="warning" />;
    }

    return <Chip size="small" label="Pending Review" color="info" />;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>

      <Card
        elevation={0}
        sx={{ background: "transparent", boxShadow: "none", border: "none" }}
      >
        <CardHeader
          sx={{
            mb: 4,
          }}
          avatar={
            <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
              <ComputerIcon fontSize="large" />
            </Avatar>
          }
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                Hardware ID Submissions
              </Typography>
            </Box>
          }
          subheader="Review and approve user hardware ID submissions for licensed products"
          action={
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadSubmissions}
              disabled={loading}
            >
              Refresh
            </Button>
          }
        />

        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by user email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                >
                  <MenuItem value="all">All Submissions</MenuItem>
                  <MenuItem value="pending">Pending Review</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                sx={{ display: "flex", alignItems: "center", height: "100%" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total: {totalCount} submissions
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          {/* Submissions Table */}
          <TableContainer
            component={Paper}
            elevation={1}
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "18%" }}>User</TableCell>
                  <TableCell sx={{ width: "25%" }}>Product</TableCell>
                  <TableCell sx={{ width: "20%" }}>Hardware ID</TableCell>
                  <TableCell sx={{ width: "12%" }}>Status</TableCell>
                  <TableCell sx={{ width: "12%" }}>Submitted</TableCell>
                  <TableCell sx={{ width: "8%" }}>Approved By</TableCell>
                  <TableCell align="center" sx={{ width: "15%" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : submissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        No hardware ID submissions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  submissions.map((submission, index) => (
                    <TableRow
                      key={submission.id}
                      hover
                      sx={{
                        "&:hover": {
                          borderBottomColor: "primary.main",
                        },
                        "& td": {
                          py: 2,
                        },
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {submission.user?.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {submission.user?.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {submission.product?.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: 1.2,
                              maxHeight: "2.4em", // 2 lines * 1.2 line-height
                              wordBreak: "break-word",
                            }}
                          >
                            {submission.product?.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              maxWidth: 180,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {submission.hardware_id}
                          </Typography>
                          <Tooltip title="Copy Hardware ID">
                            <IconButton
                              size="small"
                              onClick={() =>
                                copyToClipboard(submission.hardware_id)
                              }
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(submission)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                          {new Date(
                            submission.submitted_at,
                          ).toLocaleDateString()}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {new Date(
                            submission.submitted_at,
                          ).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {submission.confirmedBy ? (
                          <AdminInfoDisplay
                            admin={submission.confirmedBy}
                            label=""
                            showRole={false}
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <Tooltip title="Review Submission">
                            <IconButton
                              color="primary"
                              onClick={() => handleReviewSubmission(submission)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {!submission.is_confirmed && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  color="success"
                                  onClick={() => {
                                    setSelectedSubmission(submission);
                                    handleConfirmSubmission(true);
                                  }}
                                >
                                  <CheckIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    setSelectedSubmission(submission);
                                    handleConfirmSubmission(false);
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ComputerIcon color="primary" />
            <Typography variant="h6">Review Hardware ID Submission</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSubmission && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedSubmission.user?.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Product
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedSubmission.product?.name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Hardware ID
                  </Typography>
                  <TextField
                    fullWidth
                    value={selectedSubmission.hardware_id}
                    margin="dense"
                    multiline
                    rows={3}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: "monospace", fontSize: "0.875rem" },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              copyToClipboard(selectedSubmission.hardware_id)
                            }
                          >
                            <CopyIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Submitted At
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(selectedSubmission.submitted_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(selectedSubmission)}
                  </Box>
                </Grid>
                {selectedSubmission.confirmedBy && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Approved By
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <AdminInfoDisplay
                        admin={selectedSubmission.confirmedBy}
                        label=""
                        showRole={true}
                        size="medium"
                      />
                    </Box>
                    {selectedSubmission.confirmed_at && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Approved on:{" "}
                        {new Date(
                          selectedSubmission.confirmed_at,
                        ).toLocaleString()}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>

              {!selectedSubmission.is_confirmed && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Review Required:</strong> Please verify the hardware
                    ID is correct before approving. Once approved, the user will
                    be able to generate licenses for this product.
                  </Typography>
                </Alert>
              )}

              {selectedSubmission.is_confirmed && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    This hardware ID has been confirmed and locked. The user can
                    now generate licenses.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReviewDialogOpen(false)}
            disabled={processing}
          >
            Close
          </Button>
          {selectedSubmission && !selectedSubmission.is_confirmed && (
            <>
              <Button
                onClick={() => handleConfirmSubmission(false)}
                color="error"
                disabled={processing}
                startIcon={
                  processing ? <CircularProgress size={16} /> : <CloseIcon />
                }
              >
                Reject
              </Button>
              <Button
                onClick={() => handleConfirmSubmission(true)}
                color="success"
                variant="contained"
                disabled={processing}
                startIcon={
                  processing ? <CircularProgress size={16} /> : <CheckIcon />
                }
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HardwareSubmissionManagement;
